import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaRobot,
  FaMagic,
} from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function ChatInterface() {
  const [input, setInput] = useState("");
  const [showSlider, setShowSlider] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const sliderRef = useRef(null);
  const inputRef = useRef(null);

  // Handle typing animation effect
  useEffect(() => {
    if (input.length > 0 && !isTyping) {
      setIsTyping(true);
    } else if (input.length === 0 && isTyping) {
      setIsTyping(false);
    }
  }, [input, isTyping]);

  // Simulate progress bar animation during loading
  useEffect(() => {
    let interval;
    if (loading && selectedTemplate) {
      interval = setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 10 : prev));
      }, 300);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading, selectedTemplate]);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
        padding: "50px 20px",
        fontFamily: "'Inter', sans-serif",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ textAlign: "center", marginBottom: "50px", maxWidth: "800px" }}
      >
        <h1
          style={{
            fontSize: "clamp(2rem, 5vw, 3rem)",
            marginBottom: "12px",
            fontWeight: "800",
            background: "linear-gradient(90deg, #FF6B6B, #4ECDC4, #45B7D1)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Hi Welcome to RaGu
        </h1>
        <p style={{ fontSize: "clamp(1rem, 3vw, 1.3rem)", opacity: 0.85 }}>
          To assist you better, lets know each other...
        </p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "70px",
          width: "100%",
          maxWidth: "750px",
          position: "relative",
        }}
      >
        <div style={{ position: "relative", width: "100%" }}>
          <motion.input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='Describe your campaign idea (e.g., "Summer Sale")'
            aria-label="Campaign search input"
            style={{
              width: "100%",
              padding: "20px 30px",
              borderRadius: "20px",
              border: "none",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(12px)",
              fontSize: "clamp(16px, 2.5vw, 18px)",
              outline: "none",
              color: "#fff",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.15)";
              e.target.style.boxShadow = "0 8px 32px rgba(94, 53, 177, 0.4)";
            }}
            onBlur={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.1)";
              e.target.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.25)";
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
          />
          <motion.div
            animate={{
              width: isTyping ? "100%" : "0%",
              opacity: isTyping ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "3px",
              background: "linear-gradient(90deg, #FF6B6B, #4ECDC4)",
              borderRadius: "3px",
            }}
          />
        </div>
      </motion.form>
    </div>
  );
}

export default ChatInterface;
